/**
 * OutletDetail.tsx — Forensic dossier view for a single registry outlet.
 *
 * Layout:
 *   - Hero: back link, Census ID (mono, cyan glow) with copy + status + admin
 *     enrichment badge, then the outlet name as the page's largest text.
 *   - Two-column body (stacks on mobile):
 *       left  — Canonical record card with four sections
 *               (Identity / Location / Contact & Legal / Registry Metadata)
 *       right — sticky sidebar with Client coverage + Sighting timeline
 *   - Full-width Data Sources section below.
 *
 * Hides empty fields entirely (no "—" placeholders). Sections fold away when
 * every row inside is empty. Admin-only fields gated via adminMode + the
 * sources.mode === 'full' signal from the backend.
 *
 * API: GET /api/registry/outlet/{census_id}
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api';
import { theme } from '../../theme';
import { useAdmin } from '../../AdminContext';
import {
  Card,
  Tag, ConfidenceTag, MethodTag,
  IconButton, Icon,
  Skeleton,
  EmptyState, Table, type Column,
  Button,
  toast,
} from '../../components/ui';

/* ═══════════════ TYPES ═══════════════ */

interface OutletData {
  census_id: string;
  canonical_name: string;
  canonical_address: string;
  canonical_city: string;
  municipality: string;
  region: string;
  country: string;
  outlet_format: string;
  branch_name: string;
  chain_name: string;
  latitude: number | null;
  longitude: number | null;
  company_wall_status: string;
  is_chain: boolean;
  status: string;
  created_at: string | null;
  updated_at: string | null;
  first_seen: string | null;
  phone?: string;
  website?: string;
  owner_name?: string;
  opening_hours?: string;
  vat_number?: string | null;
  enrichment_score?: number;
  last_enriched_at?: string | null;
}

interface ClientRecord {
  client_name: string;
  raw_name: string;
  match_confidence: number | null;
  match_method: string;
}

interface Sighting {
  cycle_date: string;
  client_name: string;
}

interface SourceEntry {
  source_name: string;
  source_type: string;
  confidence: number | null;
  contributed_at: string | null;
}

interface SourceData {
  mode: 'full' | 'anonymized';
  fields: Record<string, SourceEntry[] | { source_count: number }>;
  total_sources: number;
}

interface DetailResponse {
  outlet: OutletData;
  clients: ClientRecord[];
  client_count: number;
  sightings: Sighting[];
  sources: SourceData | null;
}

/* ═══════════════ HELPERS ═══════════════ */

function prettyFieldName(raw: string): string {
  return raw
    .replace(/^canonical_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

const isPresent = (v: unknown): v is string =>
  typeof v === 'string' && v.trim().length > 0;

const fmtDate = (s: string | null | undefined): string =>
  s ? new Date(s).toLocaleDateString() : '';

/** Inline phone icon — Icons.tsx doesn't ship one and this page is the only consumer. */
function PhoneIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size} height={size}
      viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.8}
      strokeLinecap="round" strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

/* ═══════════════ PAGE ═══════════════ */

export default function OutletDetail() {
  const { censusId } = useParams<{ censusId: string }>();
  const navigate = useNavigate();
  const { adminMode, maskClientName, viewAsClient, modeVersion } = useAdmin();

  const [data, setData] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = 'Outlet Detail | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  useEffect(() => {
    if (!censusId) return;
    setLoading(true);
    setError(false);
    api
      .get<DetailResponse>(`/api/registry/outlet/${censusId}`)
      .then(r => setData(r.data))
      .catch(() => { setData(null); setError(true); })
      .finally(() => setLoading(false));
  }, [censusId, modeVersion]);

  /* ─── Loading ─── */
  if (loading) {
    return (
      <div style={{ padding: 32 }}>
        <Skeleton width={120} height={14} />
        <div style={{ marginTop: 16 }}>
          <Skeleton width={220} height={32} />
          <div style={{ marginTop: 10 }}>
            <Skeleton width={420} height={26} />
          </div>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 20, marginTop: 32,
        }}>
          {[0, 1].map(i => (
            <Card key={i}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {Array.from({ length: 6 }).map((_, j) => (
                  <div key={j}>
                    <Skeleton width={80} height={10} />
                    <div style={{ marginTop: 4 }}>
                      <Skeleton width={160 + (j * 20) % 80} height={14} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  /* ─── Error / not found ─── */
  if (!data) {
    return (
      <div style={{ padding: 32 }}>
        <EmptyState
          icon={<Icon.AlertCircle size={28} />}
          title={error ? 'Failed to load outlet' : 'Outlet not found'}
          description={
            error
              ? "We couldn't reach the registry. Check your connection and try again."
              : `No outlet matches Census ID ${censusId}. It may have been merged or removed.`
          }
          action={
            <Button
              variant="secondary"
              leftIcon={<Icon.ArrowLeft size={14} />}
              onClick={() => navigate('/census-database')}
            >Back to Census Database</Button>
          }
        />
      </div>
    );
  }

  const o = data.outlet;
  const isFullSourceMode = data.sources?.mode === 'full';
  // VAT visibility tracks the backend's admin gate (vat_number is null when
  // not full source mode), so prefer that signal over adminMode-from-context.
  const showVat = isFullSourceMode && isPresent(o.vat_number);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(o.census_id);
      toast.success('Census ID copied');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const openInMaps = () => {
    if (o.latitude == null || o.longitude == null) return;
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${o.latitude},${o.longitude}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  /* ─── Status badge mapping ─── */
  const statusKind: 'success' | 'danger' | 'neutral' =
    o.status === 'active' ? 'success' :
    o.status === 'suspected_closed' ? 'danger' :
    'neutral';
  const statusLabel = o.status
    ? o.status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
    : '';

  /* ─── Section presence checks (hide section when nothing to show) ─── */
  const hasIdentity =
    isPresent(o.canonical_name) || isPresent(o.chain_name) ||
    isPresent(o.branch_name)    || isPresent(o.outlet_format);

  const hasLocation =
    isPresent(o.canonical_address) || isPresent(o.canonical_city) ||
    isPresent(o.municipality)      || isPresent(o.region) ||
    isPresent(o.country)           ||
    (o.latitude != null && o.longitude != null) ||
    isPresent(o.company_wall_status);

  const hasContact =
    isPresent(o.phone) || isPresent(o.website) ||
    isPresent(o.opening_hours) || showVat || isPresent(o.owner_name);

  const hasMeta =
    isPresent(o.first_seen)        || isPresent(o.created_at) ||
    isPresent(o.last_enriched_at)  || (data.sources?.total_sources ?? 0) > 0;

  const adminViewing = adminMode && !viewAsClient;

  return (
    <div style={{ padding: '28px 32px 56px' }}>
      <style>{`
        .meridian-detail-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 24px;
          align-items: start;
        }
        @media (min-width: 1080px) {
          .meridian-detail-grid {
            grid-template-columns: minmax(0, 1.7fr) minmax(280px, 0.95fr);
            gap: 28px;
          }
          .meridian-detail-aside {
            position: sticky;
            top: 24px;
          }
        }
        .meridian-detail-hero-id {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 6px 14px 6px 12px;
          border-radius: ${theme.radius.md};
          background: ${theme.brand.cyanSoft};
          border: 1px solid ${theme.borderColor.brand};
          box-shadow: ${theme.shadow.glowSoft};
        }
        .meridian-detail-name {
          font-size: 34px;
          line-height: 1.08;
          letter-spacing: -0.025em;
        }
        @media (min-width: 720px) {
          .meridian-detail-name { font-size: 44px; }
        }
      `}</style>

      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate('/census-database')}
        style={{
          background: 'none', border: 'none', padding: 0,
          cursor: 'pointer', marginBottom: 18,
          color: theme.brand.cyan,
          fontFamily: theme.font.ui,
          fontSize: 13, fontWeight: 500,
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}
        onMouseEnter={e => { e.currentTarget.style.color = theme.brand.cyanLight; }}
        onMouseLeave={e => { e.currentTarget.style.color = theme.brand.cyan; }}
      >
        <Icon.ArrowLeft size={14} />
        Census Database
      </button>

      {/* ════════ HERO ════════ */}
      <header style={{
        marginBottom: 28,
        paddingBottom: 22,
        borderBottom: `1px solid ${theme.borderColor.subtle}`,
        position: 'relative',
      }}>
        {/* Subtle cyan wash behind the hero so it reads as a header zone */}
        <div aria-hidden style={{
          position: 'absolute', inset: '-12px -16px 0 -16px',
          background: `radial-gradient(800px 160px at 8% 0%, ${theme.brand.cyanGlowSoft}, transparent 60%)`,
          pointerEvents: 'none',
          zIndex: 0,
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Census ID + actions row */}
          <div style={{
            display: 'flex', alignItems: 'center',
            gap: 12, flexWrap: 'wrap',
            marginBottom: 14,
          }}>
            <div className="meridian-detail-hero-id">
              <span style={{
                fontFamily: theme.font.ui,
                fontSize: 10, fontWeight: 600,
                color: theme.brand.cyan,
                textTransform: 'uppercase',
                letterSpacing: theme.letterSpacing.wider,
              }}>Census ID</span>
              <span style={{
                fontFamily: theme.font.data,
                fontSize: 18, fontWeight: 700,
                color: theme.brand.cyanLight,
                letterSpacing: '-0.01em',
                textShadow: `0 0 18px ${theme.brand.cyanGlow}`,
                fontVariantNumeric: 'tabular-nums',
              }}>{o.census_id}</span>
              <IconButton
                icon={<Icon.Copy size={13} />}
                size="sm"
                onClick={copyId}
                aria-label="Copy Census ID"
                title="Copy Census ID"
              />
            </div>

            <div style={{ flex: 1 }} />

            {/* Status + enrichment */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {statusLabel && <Tag kind={statusKind}>{statusLabel}</Tag>}
              {adminViewing && o.enrichment_score != null && (
                <EnrichmentScoreBadge score={o.enrichment_score} />
              )}
            </div>
          </div>

          {/* Outlet name */}
          <h1
            className="meridian-detail-name"
            style={{
              margin: 0,
              fontFamily: theme.font.display,
              fontWeight: 700,
              color: o.canonical_name ? theme.text.primary : theme.text.tertiary,
              fontStyle: o.canonical_name ? 'normal' : 'italic',
            }}
          >
            {o.canonical_name || 'Name not yet available'}
          </h1>

          {/* Tagline row: chain / format / city snippet */}
          {(isPresent(o.chain_name) || isPresent(o.outlet_format) || isPresent(o.canonical_city)) && (
            <div style={{
              marginTop: 12,
              display: 'flex', flexWrap: 'wrap',
              alignItems: 'center', gap: 10,
              fontFamily: theme.font.ui,
              fontSize: 13,
              color: theme.text.secondary,
            }}>
              {isPresent(o.chain_name) && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Icon.Briefcase size={13} />
                  {o.chain_name}
                </span>
              )}
              {isPresent(o.outlet_format) && (
                <>
                  {isPresent(o.chain_name) && <Dot />}
                  <span>{o.outlet_format}</span>
                </>
              )}
              {isPresent(o.canonical_city) && (
                <>
                  {(isPresent(o.chain_name) || isPresent(o.outlet_format)) && <Dot />}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Icon.MapPin size={13} />
                    {o.canonical_city}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* ════════ BODY GRID ════════ */}
      <div className="meridian-detail-grid">
        {/* ─── LEFT: Canonical record ─── */}
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            {hasIdentity && (
              <Section icon={<Icon.Briefcase size={12} />} title="Identity">
                <DataRow label="Outlet name" value={o.canonical_name} emphasized />
                <DataRow label="Chain" value={o.chain_name} />
                <DataRow label="Branch" value={o.branch_name} />
                <DataRow
                  label="Outlet format"
                  value={o.outlet_format}
                  pill
                />
                {o.is_chain && (
                  <DataRowCustom label="Network">
                    <Tag kind="cyan">Chain network</Tag>
                  </DataRowCustom>
                )}
              </Section>
            )}

            {hasLocation && (
              <Section icon={<Icon.MapPin size={12} />} title="Location">
                <DataRow label="Address" value={o.canonical_address} />
                <DataRow label="City" value={o.canonical_city} />
                <DataRow label="Municipality" value={o.municipality} />
                <DataRow label="Region" value={o.region} />
                <DataRow label="Country" value={o.country} />
                {o.latitude != null && o.longitude != null && (
                  <DataRowCustom label="GPS">
                    <div style={{
                      display: 'inline-flex', alignItems: 'center',
                      gap: 12, flexWrap: 'wrap',
                    }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontFamily: theme.font.data,
                        fontSize: 13,
                        color: theme.text.primary,
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        <Icon.MapPin size={12} />
                        {o.latitude.toFixed(5)}, {o.longitude.toFixed(5)}
                      </span>
                      <button
                        type="button"
                        onClick={openInMaps}
                        style={inlineLinkStyle}
                        onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                        onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                      >
                        Open in Maps
                        <Icon.ExternalLink size={11} />
                      </button>
                    </div>
                  </DataRowCustom>
                )}
                <DataRow label="CompanyWall" value={o.company_wall_status} />
              </Section>
            )}

            {hasContact && (
              <Section icon={<Icon.Globe size={12} />} title="Contact & Legal">
                {isPresent(o.phone) && (
                  <DataRowCustom label="Phone">
                    <a
                      href={`tel:${o.phone!.replace(/\s+/g, '')}`}
                      style={{
                        ...inlineLinkStyle,
                        color: theme.text.primary,
                        fontFamily: theme.font.data,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color = theme.brand.cyanLight; }}
                      onMouseLeave={e => { e.currentTarget.style.color = theme.text.primary; }}
                    >
                      <span style={{ color: theme.brand.cyan }}><PhoneIcon size={12} /></span>
                      {o.phone}
                    </a>
                  </DataRowCustom>
                )}
                {isPresent(o.website) && (
                  <DataRowCustom label="Website">
                    <a
                      href={o.website!.startsWith('http') ? o.website! : `https://${o.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...inlineLinkStyle,
                        color: theme.brand.cyanLight,
                        wordBreak: 'break-all',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                    >
                      <Icon.Globe size={12} />
                      {o.website}
                      <Icon.ExternalLink size={10} />
                    </a>
                  </DataRowCustom>
                )}
                {isPresent(o.opening_hours) && (
                  <DataRowCustom label="Hours">
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      color: theme.text.primary,
                      fontSize: 13,
                    }}>
                      <Icon.Clock size={12} />
                      {o.opening_hours}
                    </span>
                  </DataRowCustom>
                )}
                {showVat && (
                  <DataRow
                    label="VAT (PIB)"
                    value={o.vat_number || ''}
                    mono
                  />
                )}
                <DataRow label="Owner" value={o.owner_name} />
              </Section>
            )}

            {hasMeta && (
              <Section icon={<Icon.FileText size={12} />} title="Registry metadata">
                <DataRow label="Census ID" value={o.census_id} mono />
                {statusLabel && (
                  <DataRowCustom label="Status">
                    <Tag kind={statusKind}>{statusLabel}</Tag>
                  </DataRowCustom>
                )}
                {isPresent(o.first_seen) && (
                  <DataRow label="First seen" value={fmtDate(o.first_seen)} mono />
                )}
                {isPresent(o.created_at) && (
                  <DataRow label="Created" value={fmtDate(o.created_at)} mono />
                )}
                {isPresent(o.last_enriched_at) && (
                  <DataRow
                    label="Last enriched"
                    value={fmtDate(o.last_enriched_at)}
                    mono
                  />
                )}
                {data.sources && data.sources.total_sources > 0 && (
                  <DataRowCustom label="Sources">
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      fontSize: 12,
                      color: theme.semantic.success,
                      fontWeight: 600,
                    }}>
                      <Icon.CheckCircle size={12} />
                      <span style={{ fontFamily: theme.font.data }}>
                        {data.sources.total_sources}
                      </span>
                      {' '}
                      independent {data.sources.total_sources === 1 ? 'source' : 'sources'}
                    </span>
                  </DataRowCustom>
                )}
              </Section>
            )}
          </div>
        </Card>

        {/* ─── RIGHT: Coverage + Sightings sidebar ─── */}
        <aside className="meridian-detail-aside" style={{
          display: 'flex', flexDirection: 'column', gap: 20,
        }}>
          <Card title="Client coverage">
            {adminViewing ? (
              data.clients.length === 0 ? (
                <EmptyState
                  icon={<Icon.Users size={22} />}
                  title="No clients linked"
                  description="No client has submitted this outlet yet."
                  compact
                />
              ) : (
                <>
                  <CoverageSummary count={data.clients.length} />
                  <div style={{ marginLeft: -24, marginRight: -24, marginBottom: -24 }}>
                    <Table
                      columns={clientCoverageColumns(maskClientName)}
                      data={data.clients}
                      compact
                      rowKey={(_r, i) => i}
                    />
                  </div>
                </>
              )
            ) : (
              <CoverageHero count={data.client_count} />
            )}
          </Card>

          {data.sightings.length > 0 && (
            <Card title="Sighting timeline" padding="none">
              <Table
                columns={sightingColumns(maskClientName, adminMode)}
                data={data.sightings}
                compact
                rowKey={(_r, i) => i}
              />
            </Card>
          )}
        </aside>
      </div>

      {/* ════════ DATA SOURCES (full width) ════════ */}
      {data.sources && Object.keys(data.sources.fields).length > 0 && (
        <section style={{ marginTop: 32 }}>
          <SectionHeading>
            {data.sources.mode === 'full' ? 'Data sources' : 'Data quality'}
          </SectionHeading>
          {data.sources.mode === 'full' ? (
            <SourcesFull fields={data.sources.fields as Record<string, SourceEntry[]>} />
          ) : (
            <SourcesAnonymized fields={data.sources.fields as Record<string, { source_count: number }>} />
          )}
          {data.sources.total_sources > 0 && (
            <div style={{
              marginTop: 14,
              fontSize: 12,
              color: theme.text.tertiary,
            }}>
              {data.sources.mode === 'full'
                ? <>Total independent sources: <strong style={{ color: theme.text.secondary, fontWeight: 600 }}>{data.sources.total_sources}</strong></>
                : <>This outlet has been verified by <strong style={{ color: theme.text.secondary, fontWeight: 600 }}>{data.sources.total_sources}</strong> independent {data.sources.total_sources === 1 ? 'source' : 'sources'}.</>
              }
            </div>
          )}
        </section>
      )}
    </div>
  );
}

/* ═══════════════ SUB-COMPONENTS ═══════════════ */

const inlineLinkStyle: React.CSSProperties = {
  background: 'transparent', border: 'none', padding: 0,
  color: theme.brand.cyanLight,
  fontSize: 13, fontWeight: 500,
  fontFamily: theme.font.ui,
  cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
  textDecoration: 'none',
};

function Dot() {
  return (
    <span aria-hidden style={{
      width: 3, height: 3, borderRadius: '50%',
      background: theme.text.quaternary,
      display: 'inline-block',
    }} />
  );
}

/** Cyan eyebrow + body for grouped record fields. */
function Section({
  icon, title, children,
}: { icon?: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 10, fontWeight: 700,
        color: theme.brand.cyan,
        textTransform: 'uppercase',
        letterSpacing: theme.letterSpacing.wider,
        marginBottom: 12,
        paddingBottom: 8,
        borderBottom: `1px solid ${theme.borderColor.brand}`,
      }}>
        {icon && (
          <span style={{
            display: 'inline-flex',
            color: theme.brand.cyan,
            opacity: 0.85,
          }}>{icon}</span>
        )}
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {children}
      </div>
    </div>
  );
}

/** Top-level cyan section heading for the full-width Sources block. */
function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 style={{
      fontSize: 11, fontWeight: 700,
      color: theme.brand.cyan,
      textTransform: 'uppercase',
      letterSpacing: theme.letterSpacing.wider,
      margin: '0 0 14px 0',
    }}>{children}</h3>
  );
}

/** Standard label/value row. Returns null when value is empty. */
function DataRow({
  label, value, mono = false, emphasized = false, pill = false,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
  emphasized?: boolean;
  pill?: boolean;
}) {
  if (!isPresent(value)) return null;
  if (pill) {
    return (
      <DataRowCustom label={label}>
        <span style={{
          display: 'inline-flex', alignItems: 'center',
          padding: '3px 10px',
          fontSize: 11, fontWeight: 600,
          background: theme.surface.cardHover,
          border: `1px solid ${theme.borderColor.default}`,
          borderRadius: theme.radius.pill,
          color: theme.text.primary,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
        }}>{value}</span>
      </DataRowCustom>
    );
  }
  return (
    <DataRowCustom label={label}>
      <span style={{
        fontSize: emphasized ? 14 : 13,
        fontWeight: emphasized ? 600 : 400,
        lineHeight: 1.45,
        color: theme.text.primary,
        fontFamily: mono ? theme.font.data : undefined,
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
        wordBreak: 'break-word',
      }}>{value}</span>
    </DataRowCustom>
  );
}

/** Same row layout but accepts arbitrary children for the value column. */
function DataRowCustom({
  label, children,
}: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '120px minmax(0, 1fr)',
      columnGap: 16,
      alignItems: 'baseline',
    }}>
      <span style={{
        fontSize: 10, fontWeight: 600,
        color: theme.text.tertiary,
        textTransform: 'uppercase',
        letterSpacing: theme.letterSpacing.wide,
        paddingTop: 2,
      }}>{label}</span>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

/** Enrichment score chip — admin-only. Colour codes by completeness band. */
function EnrichmentScoreBadge({ score }: { score: number }) {
  const pct = Math.round(score);
  const { fg, bg, border } = pct >= 70
    ? { fg: theme.semantic.success, bg: theme.semantic.successSoft, border: theme.semantic.successBorder }
    : pct >= 40
    ? { fg: theme.semantic.warning, bg: theme.semantic.warningSoft, border: theme.semantic.warningBorder }
    : { fg: theme.semantic.danger,  bg: theme.semantic.dangerSoft,  border: theme.semantic.dangerBorder };
  return (
    <span
      title="Data completeness score"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 11, fontWeight: 600,
        padding: '3px 9px',
        borderRadius: theme.radius.pill,
        background: bg,
        border: `1px solid ${border}`,
        color: fg,
        fontFamily: theme.font.ui,
        letterSpacing: '0.02em',
      }}
    >
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: fg,
        boxShadow: `0 0 6px ${fg}`,
      }} />
      {pct}% enriched
    </span>
  );
}

function CoverageSummary({ count }: { count: number }) {
  return (
    <div style={{
      fontSize: 13, color: theme.text.secondary,
      marginBottom: 14,
    }}>
      Linked to{' '}
      <span style={{
        fontFamily: theme.font.data,
        color: theme.text.primary,
        fontWeight: 600,
      }}>{count}</span>
      {' '}{count === 1 ? 'client submission' : 'client submissions'}.
    </div>
  );
}

function CoverageHero({ count }: { count: number }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', gap: 10,
      padding: '8px 0 4px',
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: theme.brand.cyanSoft,
        border: `1px solid ${theme.borderColor.brand}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 6,
        color: theme.brand.cyan,
      }}>
        <Icon.Users size={24} />
      </div>
      <div style={{
        fontFamily: theme.font.data,
        fontSize: 32, fontWeight: 700,
        color: theme.brand.cyanLight,
        lineHeight: 1,
        textShadow: `0 0 16px ${theme.brand.cyanGlow}`,
        fontVariantNumeric: 'tabular-nums',
      }}>{count}</div>
      <div style={{
        fontSize: 13, color: theme.text.secondary,
        maxWidth: 280, lineHeight: 1.5,
      }}>
        Covered by {count} independent {count === 1 ? 'source' : 'sources'}
      </div>
    </div>
  );
}

/** Columns for the admin-only Client Coverage table. */
function clientCoverageColumns(maskClientName: (n: string) => string): Column<ClientRecord>[] {
  return [
    {
      key: 'client_name',
      header: 'Client',
      render: r => (
        <span style={{ color: theme.text.primary, fontWeight: 500 }}>
          {maskClientName(r.client_name)}
        </span>
      ),
    },
    {
      key: 'raw_name',
      header: 'Raw name',
      render: r => (
        <span style={{ color: theme.text.secondary }}>
          {r.raw_name || '—'}
        </span>
      ),
    },
    {
      key: 'match_confidence',
      header: 'Confidence',
      width: 110,
      align: 'right',
      render: r => r.match_confidence != null
        ? <ConfidenceTag score={r.match_confidence} />
        : <span style={{ color: theme.text.tertiary }}>—</span>,
    },
    {
      key: 'match_method',
      header: 'Method',
      width: 130,
      render: r => <MethodTag method={r.match_method} />,
    },
  ];
}

/** Columns for the Sighting Timeline table.
 *  The "Reported by" column is admin-only — clients and demo users only see the cycle date. */
function sightingColumns(
  maskClientName: (n: string) => string,
  adminMode: boolean,
): Column<Sighting>[] {
  const cols: Column<Sighting>[] = [
    {
      key: 'cycle_date',
      header: 'Cycle date',
      width: 140,
      mono: true,
      render: r => (
        <span style={{
          color: theme.text.primary,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {new Date(r.cycle_date).toLocaleDateString()}
        </span>
      ),
    },
  ];
  if (adminMode) {
    cols.push({
      key: 'client_name',
      header: 'Reported by',
      render: r => (
        <span style={{ color: theme.text.secondary }}>
          {maskClientName(r.client_name)}
        </span>
      ),
    });
  }
  return cols;
}

/* ─── Data Sources: full detail mode ─── */

function SourcesFull({ fields }: { fields: Record<string, SourceEntry[]> }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {Object.entries(fields).map(([field, sources]) => (
        <div key={field}>
          <div style={{
            fontSize: 12, fontWeight: 600,
            color: theme.text.primary,
            marginBottom: 8,
            letterSpacing: '-0.005em',
          }}>{prettyFieldName(field)}</div>
          <div style={{
            background: theme.surface.panel,
            border: `1px solid ${theme.borderColor.default}`,
            borderRadius: 8,
            overflow: 'hidden',
          }}>
            {sources.map((s, i) => (
              <div key={i} style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 70px 100px',
                gap: 12,
                padding: '10px 14px',
                fontSize: 12,
                borderTop: i === 0 ? 'none' : `1px solid ${theme.borderColor.subtle}`,
                alignItems: 'center',
              }}>
                <span style={{ color: theme.text.primary }}>{s.source_name}</span>
                <span style={{ color: theme.text.tertiary, textTransform: 'capitalize' }}>
                  {s.source_type.replace(/_/g, ' ')}
                </span>
                <span style={{
                  fontFamily: theme.font.data,
                  color: s.confidence != null && s.confidence >= 0.9
                    ? theme.semantic.success
                    : theme.text.secondary,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {s.confidence != null ? `${Math.round(s.confidence * 100)}%` : '—'}
                </span>
                <span style={{
                  fontFamily: theme.font.data,
                  fontSize: 11,
                  color: theme.text.quaternary,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {s.contributed_at
                    ? new Date(s.contributed_at).toLocaleDateString()
                    : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Data Sources: anonymized mode (for client users) ─── */

function SourcesAnonymized({ fields }: { fields: Record<string, { source_count: number }> }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
      gap: 10,
    }}>
      {Object.entries(fields).map(([field, d]) => {
        const count = d.source_count;
        return (
          <div key={field} style={{
            background: theme.surface.panel,
            border: `1px solid ${theme.borderColor.default}`,
            borderRadius: 8,
            padding: '12px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12,
          }}>
            <span style={{
              fontSize: 13,
              color: theme.text.primary,
              fontWeight: 500,
            }}>{prettyFieldName(field)}</span>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: theme.semantic.success,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}>
              <Icon.Check size={12} />
              <span>
                <span style={{ fontFamily: theme.font.data }}>{count}</span>
                {' '}{count === 1 ? 'source' : 'sources'}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
